import type { FormikProps } from "formik";
import * as yup from "yup";

import { StrapiComponentType } from "@/src/type";
import { FormItemType } from "@/src/type/strapi/collection/FormItem";

function formikCheckError<T>(formik: FormikProps<T>, fieldName: string): boolean {
  return (
    //@ts-ignore
    (Boolean(formik.values?.[fieldName]) ||
      //@ts-ignore
      Boolean(formik.touched?.[fieldName])) &&
    //@ts-ignore
    Boolean(formik.errors?.[fieldName])
  );
}

function formikErrorMessage<T>(formik: FormikProps<T>, fieldName: string): string {
  if (formikCheckError<T>(formik, fieldName)) {
    //@ts-ignore
    return typeof formik.errors?.[fieldName] === "string"
      ? //@ts-ignore
        formik.errors?.[fieldName]
      : "";
  }
  return "";
}

export function displayPhoneNumber(value: string | number, formatBreakOne?: number, formatBreakTwo?: number) {
  if (!value) {
    return value;
  }
  const _one = formatBreakOne ?? 3;
  const _two = formatBreakTwo ?? 6;
  const clean = `${value}`.replace(/\D[^\.]/g, "");
  const withDashes = (clean.slice(0, _one) + "-" + clean.slice(_one, _two) + "-" + clean.slice(_two)).replace("--", "-");
  return withDashes.slice(-1) === "-" ? withDashes.substring(0, withDashes.length - 1) : withDashes;
}

export function handlePhoneNumber(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setFieldValue: any) {
  const value = event.target.value.replace(/[^0-9.]/g, "");
  setFieldValue("phoneNumber", value);
}

export function displayCountryCode(value: string | number) {
  if (!value) {
    return value;
  }
  return `+${value}`;
}

export function handleCountryCode(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setFieldValue: any) {
  const inputValue = event.target.value;
  const lastChar = inputValue.slice(-1);
  const stringValues = "1234567890";

  const isLastCharValid = stringValues.includes(lastChar);
  const formattedValue = inputValue.replace("+", "");

  if (!isLastCharValid) {
    setFieldValue("countryCode", formattedValue.slice(0, -1));
  } else {
    setFieldValue("countryCode", formattedValue);
  }
}

export function displayInstagram(value: string) {
  if (!value || value === "@") {
    return value;
  }
  return `@${value}`;
}

export function handleInstagram(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setFieldValue: any) {
  //Allow user to type @ to start the instagram handle
  if (event.target.value === "@") {
    setFieldValue("instagram", "@");
    return;
  }
  const value = event.target.value.replace(/[@\s]/g, "");
  setFieldValue("instagram", value);
}

export function validationRulesToYupSchema(step: Pick<StrapiComponentType.Object.DynamicFormStep, "form_items">) {
  const schema = yup.object();
  const schemaFields: Record<string, yup.AnySchema> = {};

  for (const item of step?.form_items?.data ?? []) {
    const fieldName = item.attributes.definedProperty ?? item.attributes.jsonPropertyName;
    const fieldLabel = item.attributes.label || fieldName;

    let fieldSchema;
    switch (item.attributes.type) {
      case FormItemType.Text:
      case FormItemType.TextArea:
      case FormItemType.PhoneNumber:
      case FormItemType.Dropdown:
        fieldSchema = yup.string().typeError(`${fieldLabel} is required`);
        break;
      case FormItemType.Checkbox:
        fieldSchema = yup.boolean().typeError(`${fieldLabel} is required`);
        break;
      case FormItemType.DatePicker:
        fieldSchema = yup.date().typeError(`${fieldLabel} must be a date`);
        break;
      case FormItemType.Number:
        fieldSchema = yup.number().typeError(`${fieldLabel} must be a number`);
        break;
    }

    if (!fieldSchema) {
      continue;
    }

    const validation = item.attributes.validation;
    if (validation?.required) {
      const message = `${fieldLabel} is required`;
      fieldSchema = fieldSchema.required(message);
    }

    if (validation?.min && fieldSchema instanceof yup.StringSchema) {
      const message = `${fieldLabel} too short`;
      fieldSchema = fieldSchema.min(validation.min, message);
    }

    if (validation?.min && fieldSchema instanceof yup.NumberSchema) {
      const message = `Must be greater than ${validation.min}`;
      fieldSchema = fieldSchema.min(validation.min, message);
    }

    if (validation?.max && fieldSchema instanceof yup.StringSchema) {
      const message = `${fieldLabel} too long`;
      fieldSchema = fieldSchema.max(validation.max, message);
    }

    if (validation?.max && fieldSchema instanceof yup.NumberSchema) {
      const message = `Must be less than ${validation.max}`;
      fieldSchema = fieldSchema.max(validation.max, message);
    }

    if (validation?.format === "email" && fieldSchema instanceof yup.StringSchema) {
      fieldSchema = fieldSchema.email("Valid email is required");
    }

    if (validation?.format === "url" && fieldSchema instanceof yup.StringSchema) {
      fieldSchema = fieldSchema.url("Valid URL is required");
    }

    if (validation?.regex && fieldSchema instanceof yup.StringSchema) {
      fieldSchema = fieldSchema.matches(new RegExp(validation.regex), validation.regexPatternErrorMessage ?? "Must be a valid format");
    }

    schemaFields[fieldName] = fieldSchema;
  }

  return schema.shape(schemaFields);
}

export default {
  formikCheckError,
  formikErrorMessage,
  displayPhoneNumber,
  handlePhoneNumber,
  displayCountryCode,
  handleCountryCode,
  displayInstagram,
  handleInstagram,
  validationRulesToYupSchema,
};
